import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { config } from '~/config';
import keycloakInstance from './keycloak-instance';
import { messageIndicatesStudyPlanNotFound } from './study-plan-errors';

const api = axios.create({
  baseURL: config.api.url,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = keycloakInstance.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const DEFAULT_ERROR_MESSAGE = 'Algo deu errado. Por favor, tente novamente mais tarde.';

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  SERVER_ERROR_MIN: 500,
} as const;

const TECHNICAL_MESSAGE_PATTERN = /^Request failed with status code \d+$/i;

type ApiErrorLike = {
  message?: string;
  response?: { status?: number; data?: { message?: string } };
  config?: AxiosRequestConfig & { suppressGlobalErrorToast?: boolean; _retry?: boolean };
};

function isHandledHttpStatus(status?: number): boolean {
  if (status === undefined) return false;
  if (status < HTTP_STATUS.BAD_REQUEST) return false;
  return status < HTTP_STATUS.SERVER_ERROR_MIN || status >= HTTP_STATUS.SERVER_ERROR_MIN;
}

function isTechnicalOrEmptyMessage(message?: string): boolean {
  if (!message?.trim()) return true;
  return TECHNICAL_MESSAGE_PATTERN.test(message.trim());
}

function resolveUserMessage(error: ApiErrorLike): string {
  const apiMessage = error.response?.data?.message?.trim();
  if (apiMessage && !isTechnicalOrEmptyMessage(apiMessage)) return apiMessage;
  const errorMessage = error.message?.trim();
  if (errorMessage && !isTechnicalOrEmptyMessage(errorMessage)) return errorMessage;
  return DEFAULT_ERROR_MESSAGE;
}

function normalizeErrorMessage(error: ApiErrorLike, message: string): void {
  error.message = message;
  if (!error.response?.data || typeof error.response.data !== 'object') return;
  error.response.data.message = message;
}

function showGlobalErrorToast(message: string): void {
  if (typeof window === 'undefined') return;
  toast.error(message);
}

function shouldSuppressStudyPlanNotFoundToast(error: ApiErrorLike, message: string): boolean {
  if (!messageIndicatesStudyPlanNotFound(message)) return false;
  const url = error.config?.url ?? '';
  return url.includes('/study-plans') || url.includes('/study-schedules/status');
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;

    const shouldAttemptAuthRecovery =
      status === HTTP_STATUS.UNAUTHORIZED || status === HTTP_STATUS.FORBIDDEN;

    if (shouldAttemptAuthRecovery) {
      const originalConfig = (error as ApiErrorLike).config;
      const hasToken = !!keycloakInstance.token;

      if (!originalConfig?._retry && hasToken) {
        try {
          if (originalConfig) originalConfig._retry = true;
          await keycloakInstance.updateToken(5);
          return api(originalConfig ?? {});
        } catch {
          return Promise.reject(error);
        }
      }
    }

    if (status === HTTP_STATUS.FORBIDDEN) {
      const url = error.config?.url ?? '';
      const isActivitiesRoute =
        (url.includes('/quizzes') && !url.includes('/quizzes/access')) ||
        url.includes('/error-notebook');
      if (isActivitiesRoute) {
        window.location.href = '/';
        return Promise.reject(error);
      }
    }

    if (!isHandledHttpStatus(status)) {
      return Promise.reject(error);
    }

    const normalizedError = error as ApiErrorLike;
    const userMessage = resolveUserMessage(normalizedError);
    normalizeErrorMessage(normalizedError, userMessage);
    const shouldSuppressStudyPlanToast = shouldSuppressStudyPlanNotFoundToast(
      normalizedError,
      userMessage,
    );

    if (!normalizedError.config?.suppressGlobalErrorToast && !shouldSuppressStudyPlanToast) {
      showGlobalErrorToast(userMessage);
    }

    return Promise.reject(error);
  },
);

export default api;
